import { z } from 'zod';
/**
 * The four optional matching fields — their controls, their copy, and the two places they are
 * rendered (`MEM-045`).
 *
 * ## What these are and why they came back
 *
 * The specification's "REQUIRED AT REGISTRATION — Enables Matching & Follow-Up" table lists
 * professional role **and industry**, a conversation starter, and a social comfort level, each
 * with an interview finding attached. The 2026-08-21 decision to follow the prototype's five
 * steps dropped all three, and `docs/plans/stage-m1-member-registration.md` recorded what that
 * cost: 16 of the platform's 100 weight points with no input at all and a further 10 behind an
 * unbuilt screen, measured at a reachable ceiling of 74 against a threshold of 62.
 *
 * The 2026-08-22 decision puts them back **on the existing five steps, optional**. Not a sixth
 * step: the three-minute guardrail is why they were cut, and a sixth step trades it straight back.
 *
 * ## One implementation, two mount points
 *
 * `ORG-013` is the pattern and its header carries the rule this file follows: *"Keeping the
 * difference in a table rather than in branches inside the component is what stops the two
 * drifting into two editors."* {@link MATCHING_FIELDS_MOUNTS} is that table. A mount point names
 * the fields it renders and the chrome around them; the React component reads the row and renders
 * it, and has no `if (mountPoint === …)` in it.
 *
 * The registration wizard is three mount points rather than one because the fields live on three
 * different steps — which is the client's placement, each field beside the question it belongs
 * with — and the preferences panel is the fourth, rendering all of them together.
 *
 * ## Why this module is in `contracts`
 *
 * The same three consumers as `member-registration.ts`: the wizard renders from it, the API
 * validates against it, and the tests assert it. `apps/web` has no DOM test runner, so anything
 * asserted about copy or field placement has to be reachable from a plain `.ts` file.
 */
/* ============================================================================
   The fields
   ============================================================================ */
export const MEMBER_OPTIONAL_MATCHING_FIELD_IDS = [
    'industry',
    'careerStage',
    'conversationStarter',
    'socialComfort',
];
/** The vocabularies these fields select from. All three are authored in the admin console
 * (`ADM-001`) and seeded; they were never deleted, which is why restoring collection is a form
 * control rather than a migration. */
export const MEMBER_OPTIONAL_MATCHING_VOCABULARIES = [
    'industry',
    'career-stage',
    'social-comfort',
];
/**
 * The four fields.
 *
 * **The conversation starter feeds no weight and is here anyway.** It is not scored — the five
 * weights are fixed by `platform-config` and adding a sixth is an operator-facing change with its
 * own story. What it powers is the specification's own answer: *"Pre-event match cards; in-event
 * introduction support"*, validated across nearly every interview as the thing that "directly
 * reduces the anxiety of 'what do I say?'". `MEM-025`'s match card and `MEM-011`'s attendee
 * preview are the surfaces that will render it; collecting it now is what stops those stories
 * shipping against an empty column.
 */
export const MEMBER_OPTIONAL_MATCHING_FIELDS = [
    {
        id: 'industry',
        attribute: 'industryTermId',
        control: 'taxonomy-single',
        vocabulary: 'industry',
        step: 'basics',
        label: 'Industry',
        hint: 'Helps us introduce you to people in and around your field.',
        placeholder: 'Choose an industry',
        maxLength: null,
        weight: 'proximity',
    },
    {
        id: 'careerStage',
        attribute: 'careerStageTermId',
        control: 'taxonomy-single',
        vocabulary: 'career-stage',
        step: 'basics',
        label: 'Career stage',
        // "including 'in transition'" is the specification's own parenthesis, and the seeded
        // vocabulary carries a term for it. Saying so here is what stops somebody between jobs
        // reading this field as one they are not entitled to answer.
        hint: "Where you are right now — including in transition. It's how we tell a mentor from a peer.",
        placeholder: 'Choose a career stage',
        maxLength: null,
        weight: 'proximity',
    },
    {
        id: 'conversationStarter',
        attribute: 'conversationStarter',
        control: 'free-text',
        vocabulary: null,
        step: 'goal',
        label: "One thing you're excited to talk about",
        hint: 'This is what we put on your match card, so the other person has somewhere to start.',
        placeholder: "Right now I can't stop thinking about…",
        maxLength: 120,
        weight: null,
    },
    {
        id: 'socialComfort',
        attribute: 'socialComfortTermId',
        control: 'taxonomy-single',
        vocabulary: 'social-comfort',
        step: 'connecting',
        label: 'How you are in a room',
        hint: "There's no right answer. We use it to pair people who'll actually start talking.",
        placeholder: 'Choose what fits',
        maxLength: null,
        weight: 'socialComfort',
    },
];
export function optionalMatchingField(id) {
    const found = MEMBER_OPTIONAL_MATCHING_FIELDS.find((field) => field.id === id);
    if (found === undefined)
        throw new Error(`"${id}" is not an optional matching field`);
    return found;
}
export function optionalMatchingFieldsForStep(step) {
    return MEMBER_OPTIONAL_MATCHING_FIELDS.filter((field) => field.step === step);
}
/** The four attribute names, which is what the storage allow-list and the preferences route both
 * need. Derived rather than restated, so a fifth field is one entry above and nothing else. */
export const MEMBER_OPTIONAL_MATCHING_ATTRIBUTES = MEMBER_OPTIONAL_MATCHING_FIELDS.map((field) => field.attribute);
/* ============================================================================
   The mount points
   ============================================================================ */
export const MATCHING_FIELDS_MOUNT_POINTS = [
    'registration-basics',
    'registration-goal',
    'registration-connecting',
    'preferences',
];
/**
 * What each mount renders, and the chrome around it.
 *
 * `ORG-013`'s `MOUNT_POINT_CHROME` with the same job. The registration rows differ from each
 * other only in which field they carry, which is exactly the kind of difference that becomes a
 * branch if it is allowed to.
 */
export const MATCHING_FIELDS_MOUNTS = {
    'registration-basics': {
        fields: ['industry', 'careerStage'],
        heading: null,
        blurb: null,
        saveAction: null,
        savedNotice: null,
    },
    'registration-goal': {
        fields: ['conversationStarter'],
        heading: null,
        blurb: null,
        saveAction: null,
        savedNotice: null,
    },
    'registration-connecting': {
        fields: ['socialComfort'],
        heading: null,
        blurb: null,
        saveAction: null,
        savedNotice: null,
    },
    preferences: {
        fields: ['industry', 'careerStage', 'conversationStarter', 'socialComfort'],
        heading: 'Improve your matches',
        // "Three", not four: the screen draws social comfort in its own card higher up and leaves it
        // out of this block (`omitAttributes`), so the block shows three (TW 44261071).
        blurb: "These three are optional and you can change them whenever you like. Every one you answer is one more thing we can match on — and the ones you leave blank simply aren't counted.",
        saveAction: 'Save',
        savedNotice: 'Saved. This applies to your next event.',
    },
};
/**
 * The vocabularies the wizard needs **before an account exists** (`MEM-045`).
 *
 * Step 1 creates the account, so everything it renders is rendered to somebody with no session —
 * and two of the four optional fields live on step 1. `GET /registration/vocabularies` is
 * authenticated, deliberately, on the argument that "by the time a member needs these they have an
 * account". True for steps 2 to 5; not true for step 1, which is what this list exists to name.
 *
 * These terms therefore ride on the **public** wizard-contract route instead. What that discloses
 * is a list like "Technology / Education / Finance" and "Founder / Early career" — platform
 * vocabulary, the same for every caller, alongside the step copy and field limits that route
 * already serves anonymously. It carries no member data and no configuration state an operator
 * would call private, which is the line the authenticated route was drawn against.
 *
 * Derived from the field table rather than listed, so a fifth optional field on step 1 is picked
 * up without a second edit here.
 */
export const MEMBER_PRE_ACCOUNT_VOCABULARIES = MEMBER_OPTIONAL_MATCHING_FIELDS.filter((field) => field.step === 'basics' && field.vocabulary !== null).map((field) => field.vocabulary);
/** Every mount that is part of the registration wizard, so a caller can ask "is this the wizard?"
 * without listing them and getting it wrong when a fifth field lands on a fourth step. */
export function isRegistrationMount(mountPoint) {
    return mountPoint !== 'preferences';
}
/**
 * Which of a mount's fields have somewhere to draw their options from (`MEM-045` AC-2).
 *
 * **An unpopulated vocabulary hides its control and blocks nothing.** `MEM-004`'s not-ready state
 * exists because a member cannot finish a step whose only question has no answers; these questions
 * are optional, so an unauthored `industry` vocabulary is not a step that cannot be answered — it
 * is a question that is not being asked. Rendering the apology there would stop a wizard over
 * nothing, and rendering an empty `<select>` is the defect `MEM-004` AC-3 names.
 *
 * A function here rather than a condition inside the component, for the reason this whole module
 * is here: `apps/web` has no DOM test runner, so a rule expressed as JSX is a rule nothing can
 * assert.
 */
export function visibleFieldsFor(mountPoint, terms) {
    return MATCHING_FIELDS_MOUNTS[mountPoint].fields
        .map((id) => optionalMatchingField(id))
        .filter((field) => field.vocabulary === null || (terms[field.vocabulary] ?? []).length > 0);
}
/* ============================================================================
   The wire shape for editing them after registration
   ============================================================================ */
/**
 * The preferences patch (`MEM-045` AC-4).
 *
 * **`.strict()` is the acceptance criterion.** "Exactly these four fields, and no other
 * registration answer" is enforced by refusing an unknown key rather than by filtering one out
 * silently: a client sending `interestTagTermIds` to this route has misunderstood something, and
 * a 400 says so where a quiet drop would not. The registration wizard's own allow-list makes the
 * opposite trade for the opposite reason — there the forbidden set is open-ended.
 *
 * **`null` clears, absent leaves alone, and `''` clears too.** Three states, because "I never
 * touched this" and "I want this empty again" are different intentions and a member who chose an
 * industry must be able to un-choose it. `''` is accepted alongside `null` because that is what a
 * `<select>` with an empty option submits, and normalising it in one place beats every caller
 * remembering to.
 */
const clearable = (schema) => schema.nullable().optional();
export const memberMatchingPreferencesSchema = z
    .object({
    industryTermId: clearable(z.string().trim().max(120)),
    careerStageTermId: clearable(z.string().trim().max(120)),
    conversationStarter: clearable(z.string().trim().max(120)),
    socialComfortTermId: clearable(z.string().trim().max(120)),
})
    .strict();
/**
 * Split a patch into what to write and what to erase.
 *
 * One helper, used by the wizard's step save and by the preferences route, so the two cannot
 * disagree about what an empty answer means. Storing `''` would be the subtle wrong answer: the
 * scorer tests `industryTermId !== undefined`, so an empty string is a *present* industry that
 * matches nobody, and the pair would be scored on a signal neither member gave.
 */
export function partitionOptionalMatchingAnswers(patch) {
    const set = {};
    const clear = [];
    for (const attribute of MEMBER_OPTIONAL_MATCHING_ATTRIBUTES) {
        if (!(attribute in patch))
            continue;
        const value = patch[attribute];
        if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
            clear.push(attribute);
            continue;
        }
        if (typeof value === 'string')
            set[attribute] = value.trim();
    }
    return { set, clear };
}
/* ============================================================================
   Copy
   ============================================================================ */
/**
 * What the wizard says about answering none of them (`MEM-045` AC-1).
 *
 * Rendered once, on the preferences panel and nowhere in the wizard: on a step, the per-field
 * "· optional" marker already says it, and a wizard that reassures a member four times about
 * four fields has made them feel interrogated by the reassurance.
 */
export const MEMBER_OPTIONAL_MATCHING_SKIP_NOTICE = "Skip anything you'd rather not answer. A blank one isn't held against you — it's left out of the comparison entirely.";
/** Appended to each registration step's "why we ask" panel, so `MEM-001` AC-4's promise that the
 * explainer says why *each* piece is collected survives the four new pieces. */
export const MEMBER_OPTIONAL_MATCHING_WHY_WE_ASK = {
    industry: 'Industry is how we tell "two people in climate tech" from "two people who both ticked climate". It is optional, and if you leave it blank we compare you on everything else instead of counting it against you.',
    careerStage: 'Career stage is the one signal that says whether two people meet as peers or as mentor and mentee. "In transition" is a real answer and one of the most useful ones.',
    conversationStarter: "Whatever you write here goes on your match card, in your words. It is the difference between somebody walking up to you with a question and somebody walking up to you with 'so, what do you do?'.",
    socialComfort: "We pair complementary answers rather than identical ones: somebody who'd appreciate a warm intro is matched with somebody happy to make the first move, not with another person waiting to be approached.",
};
//# sourceMappingURL=member-matching-fields.js.map